import React, { useEffect, useMemo, useState } from "react";

const AD_PARENT_SLUG = "quang-cao";

const DESKTOP_REPLACE_POSITION_KEYS = [
  "desktop_replace_posts_left",
  "desktop_replace_posts_right",
  "desktop_replace_events_left",
  "desktop_replace_events_right",
  "desktop_replace_news_left",
  "desktop_replace_news_right",
  "desktop_replace_products_left",
  "desktop_replace_products_right",
];

const getMediaCategoryId = (media) => {
  return (
    media?.mediaCategoryId ||
    media?.media_category_id ||
    media?.id_media_category ||
    media?.MediaCategoryId ||
    null
  );
};

const getImageUrl = (media) => {
  return media?.url || media?.imageUrl || media?.image_url || media?.path || "";
};

const getIframeUrl = (url) => {
  const value = String(url || "").trim();

  if (!value) {
    return "";
  }

  if (value.includes("youtube.com/watch?v=")) {
    return value.replace("watch?v=", "embed/")?.replace(/&ab_channel=[^&]*/, "");
  }

  if (value.includes("youtu.be/")) {
    const videoId = value.split("youtu.be/")[1]?.split("?")[0];

    return videoId ? `https://www.youtube.com/embed/${videoId}` : value;
  }

  if (value.includes("drive.google.com/file/d/") && value.includes("/view")) {
    return value.replace(/\/view.*$/, "/preview");
  }

  if (value.includes("drive.google.com/file/d/") && !value.includes("/preview")) {
    return value.replace(/\/?$/, "/preview");
  }

  return value;
};

const useAutoIndex = (length, delay = 1500) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!length || length <= 1) {
      setIndex(0);

      return;
    }

    const timer = setInterval(() => {
      setIndex((prev) => {
        const next = prev + 1;

        return next >= length ? 0 : next;
      });
    }, delay);

    return () => {
      clearInterval(timer);
    };
  }, [length, delay]);

  return index;
};

export const getAdvertisementCategories = (categories = []) => {
  const parent = categories.find((category) => category.slug === AD_PARENT_SLUG);

  if (!parent) {
    return [];
  }

  return categories.filter(
    (category) => Number(category.parentId) === Number(parent.id)
  );
};

const getCategoryMedias = (category, medias = []) => {
  return medias.filter(
    (media) => Number(getMediaCategoryId(media)) === Number(category.id)
  );
};

export const hasDesktopAdvertisements = (categories = [], medias = []) => {
  const adCategories = getAdvertisementCategories(categories);

  return adCategories.some((category) => {
    if (!DESKTOP_REPLACE_POSITION_KEYS.includes(category.positionKey)) {
      return false;
    }

    return getCategoryMedias(category, medias).some((media) => getImageUrl(media));
  });
};

export const hasAdvertisementByPosition = ({
  positionKey,
  categories = [],
  medias = [],
}) => {
  const category = getAdvertisementCategories(categories).find(
    (item) => item.positionKey === positionKey
  );

  if (!category) {
    return false;
  }

  return getCategoryMedias(category, medias).some((media) => getImageUrl(media));
};

const AdvertisementItem = ({ category, medias, mobile = false }) => {
  const validMedias = useMemo(() => {
    return (medias || []).filter((media) => getImageUrl(media));
  }, [medias]);

  const currentIndex = useAutoIndex(validMedias.length);

  const currentMedia = validMedias[currentIndex];

  if (!currentMedia) {
    return null;
  }

  const mediaUrl = getImageUrl(currentMedia);

  if (category.type === "video") {
    return (
      <div className="w-full h-full overflow-hidden bg-black">
        <iframe
          src={getIframeUrl(mediaUrl)}
          title={currentMedia.title || category.name}
          className={mobile ? "w-full aspect-video block" : "w-full h-full block"}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className={mobile ? "w-full overflow-hidden" : "w-full h-full overflow-hidden"}>
      <img
        src={mediaUrl}
        alt={currentMedia.title || category.name}
        className={
          mobile
            ? "w-full h-auto object-contain block"
            : "w-full h-full object-cover block"
        }
        loading="lazy"
      />
    </div>
  );
};

export const DesktopAdvertisementReplaceSlot = ({
  positionKey,
  categories = [],
  medias = [],
  className = "",
}) => {
  const category = getAdvertisementCategories(categories).find(
    (item) => item.positionKey === positionKey
  );

  if (!category) {
    return null;
  }

  const categoryMedias = getCategoryMedias(category, medias);

  if (!categoryMedias.length) {
    return null;
  }

  return (
    <div className={`hidden lg:block w-full h-full overflow-hidden self-stretch ${className}`}>
      <AdvertisementItem category={category} medias={categoryMedias} />
    </div>
  );
};

export const MobileAdvertisementSlot = ({
  positionKey,
  categories = [],
  medias = [],
}) => {
  const adCategories = getAdvertisementCategories(categories).filter(
    (category) => category.mobilePositionKey === positionKey
  );

  if (!adCategories.length) {
    return null;
  }

  return (
    <div className="block xl:hidden w-full overflow-hidden my-4">
      {adCategories.map((category) => {
        const categoryMedias = getCategoryMedias(category, medias);

        if (!categoryMedias.length) {
          return null;
        }

        return (
          <div key={category.id} className="w-full overflow-hidden mb-4">
            <AdvertisementItem
              category={category}
              medias={categoryMedias}
              mobile={true}
            />
          </div>
        );
      })}
    </div>
  );
};
